import {
  AbstractProduct, Attribute, Product, Selector,
} from 'src/store/design/types';

const SIZE_SELECTOR_TYPES = ['product-size'];

export const isSizeSelector = (s: Selector): boolean => (!!s.type && SIZE_SELECTOR_TYPES.includes(s.type)) || /size/i.test(s.typeName);

export const findSizeSelector = (selectors: Selector[] = []): Selector | undefined => selectors.find(isSizeSelector);

/**
 * The variant-defining attribute typeNames (GarmentSize/Colour/Print), taken
 * from the union of editor + PDP selectors so a dimension (e.g. colour) can't be
 * dropped if one selector set omits it. NOT every attribute a product carries.
 */
export const getVariantTypeNames = (abstractProduct?: AbstractProduct): string[] => {
  const selectors: Selector[] = [
    ...(abstractProduct?.editorSelectors || []),
    ...(abstractProduct?.productPageSelectors || []),
  ];
  return [...new Set(selectors.map((s) => s.typeName))];
};

/**
 * Resolve the concrete product for a size by matching only the variant
 * dimensions (with the size overridden to `sizeValue`). Matching the full
 * attribute bag would over-constrain (products also carry editor flags,
 * ProductStatus, a randomized test attr, etc.) and wrongly mark existing
 * variants unavailable. No colour-agnostic fallback — returns undefined when
 * the size isn't available for the current selection.
 */
export const resolveSizeVariant = (
  products: Product[],
  variantTypeNames: string[],
  sizeTypeName: string,
  sizeValue: string,
  selectedAttributes: { [typeName: string]: Attribute },
): Product | undefined => {
  const target: Attribute[] = variantTypeNames
    .map((tn) => (tn === sizeTypeName ? { typeName: tn, value: sizeValue } : selectedAttributes[tn]))
    .filter((a): a is Attribute => !!a);

  return products.find((p) => target.every(
    (t) => p.attributes.some((a) => a.typeName === t.typeName && a.value === t.value),
  ));
};
